/**
 * Lazy-loads the Plaid Link script (cdn.plaid.com is allowed by helmet CSP).
 * Only loads it once per page-load and caches the promise.
 */

interface PlaidLinkHandler {
  open: () => void
  exit: () => void
}

interface PlaidLinkOptions {
  token: string
  receivedRedirectUri?: string
  onSuccess: (publicToken: string, metadata: unknown) => void
  onExit?: (err: unknown, metadata: unknown) => void
}

interface PlaidGlobal {
  create: (opts: PlaidLinkOptions) => PlaidLinkHandler
}

declare global {
  interface Window {
    Plaid?: PlaidGlobal
  }
}

let plaidPromise: Promise<PlaidGlobal> | null = null

export function loadPlaid(): Promise<PlaidGlobal> {
  if (window.Plaid) return Promise.resolve(window.Plaid)
  if (plaidPromise) return plaidPromise
  plaidPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-plaid-link]')
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.Plaid) resolve(window.Plaid)
        else reject(new Error('Plaid script loaded but global missing.'))
      })
      existing.addEventListener('error', () => reject(new Error('Plaid script failed to load.')))
      return
    }
    const s = document.createElement('script')
    s.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js'
    s.async = true
    s.dataset.plaidLink = '1'
    s.onload = () => {
      if (window.Plaid) resolve(window.Plaid)
      else reject(new Error('Plaid script loaded but global missing.'))
    }
    s.onerror = () => reject(new Error('Plaid script failed to load.'))
    document.body.appendChild(s)
  })
  return plaidPromise
}
